/**
 * Utilità per il flusso di impersonificazione agente (system-admin).
 *
 * Meccanismo:
 * 1. Admin chiama POST /admin/companies/:cid/agents/:aid/impersonate → riceve payload
 * 2. Frontend serializza il payload in localStorage con chiave one-time (`imp_<random>`)
 * 3. Apre nuovo tab su `/?impersonate=<key>`
 * 4. Il nuovo tab legge + cancella il payload, applica setAuth e naviga alla home
 *
 * Il localStorage è condiviso fra tab dello stesso origin, quindi il tab figlio
 * può leggere ciò che il tab padre ha scritto. La chiave viene cancellata immediatamente
 * dopo la lettura per evitare riusi accidentali.
 */

const IMPERSONATION_KEY_PREFIX = 'imp_';

export interface ImpersonationPayload {
  accessToken: string;
  user: {
    sub: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    globalRole: string | null;
    b2bRole: string | null;
    companyRole: string | null;
    requiresCompanySelection: boolean;
    agentCode?: string | null;
  };
  companies: Array<{
    companyId: string;
    role: string;
    name: string;
    taxCode?: string | null;
  }>;
  selectedCompanyId: string;
}

/**
 * Serializza il payload di impersonificazione in localStorage.
 *
 * @param payload - Dati restituiti dall'endpoint impersonate
 * @returns Chiave one-time da appendere come query param `?impersonate=<key>`
 */
export function storeImpersonationPayload(payload: ImpersonationPayload): string {
  const key = IMPERSONATION_KEY_PREFIX + Math.random().toString(36).slice(2, 12);
  try {
    localStorage.setItem(key, JSON.stringify(payload));
  } catch {
    // localStorage pieno o non disponibile — il tab non potrà fare impersonation
  }
  return key;
}

/**
 * Legge e rimuove atomicamente il payload di impersonificazione da localStorage.
 *
 * @param key - Chiave one-time dal query param `?impersonate=<key>`
 * @returns Payload se trovato e valido, null altrimenti
 */
export function consumeImpersonationPayload(key: string): ImpersonationPayload | null {
  if (!key.startsWith(IMPERSONATION_KEY_PREFIX)) {
    return null;
  }
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  localStorage.removeItem(key); // Consuma subito (one-time)
  try {
    return JSON.parse(raw) as ImpersonationPayload;
  } catch {
    return null;
  }
}
